// Event type constants and mapping
export const EVENT_TYPES = {
  // User related events (1-99)
  USER_REGISTERED: 1,
  USER_ID_VALIDATED: 2,
  USER_BANK_ACCOUNT_SYNCED: 3,
  USER_API_CSRD_SYNCED: 4,
  
  // Document and imagery events (100-199)
  DOCUMENT_UPLOADED: 100,
  PROJECT_DOCUMENTATION_UPLOADED: 101,
  PROJECT_DOCUMENTATION_PROCESSED: 102,
  SATELLITE_ORDINARY_IMAGERY_REQUEST_SENT: 103,
  SATELLITE_URGENT_IMAGERY_REQUEST_SENT: 104,
  SATELLITE_IMAGERY_RECEIVED: 105,
  SATELLITE_IMAGERY_PROCESSED: 106,
  AERIAL_LIDAR_SCANNING_PERFORMED: 107,
  AERIAL_LIDAR_IMAGERY_PROCESSED: 108,
  
  // Monitoring and reporting (200-299)
  PD_MONITORING_REPORT_RECEIVED: 200,
  PD_REPORT_REQUESTED: 201,
  PD_URGENT_REPORT_REQUESTED: 202,
  PD_URGENT_REPORT_RECEIVED: 203,
  
  // Red flags (300-399)
  REDFLAG_PD_ACTIVITY: 300,
  REDFLAG_PROJECT_ACTIVITY: 301,
  REDFLAG_PROJECT_UNAUTHORISDED_CUTTING: 302,
  REDFLAG_PROJECT_THINNING_QUOTA: 303,
  REDFLAG_REVERSAL_EVENT_FIRE: 304,
  REDFLAG_REVERSAL_EVENT_BIOHAZARD: 305,
  REDFLAG_REVERSAL_EVENT_OTHER: 306,
  REDFLAG_BENEFICIARY: 307,
  
  // Project lifecycle events (400-499)
  PROJECT_REGISTERED: 400,
  PLANTED: 401,
  VALIDATION_VERIFICATION_COMPLETED: 402,
  SELF_ASSESSMENT: 403,
  SATELLITE_IMAGERY: 404,
  GROUND_TRUTHING: 405,
  
  // Credit lifecycle events (500-599)
  CREDIT_ISSUANCE: 500,
  CREDIT_TRANSFER: 501,
  CREDIT_RETIRED: 502,
  CREDIT_REVERTED: 503,
  CREDIT_AFFECTED_REPLACED: 504,
  CREDIT_LISTED: 505,
  CREDIT_SOLD: 506,
  CREDIT_MATCHED: 507,  // Order matched in exchange — emitted at fill time, before settlement
  CREDIT_CORRECTED: 508,  // Metadata-only correction (sinkId/vintageId updated, tokens stay ACTIVE)
  
  // Assessment and reporting (600-699)
  CO2_BASELINE_CALCULATION: 600,
  DAMAGE_REPORT: 601,
  DAMAGE_CONFIRMED: 602,
  REVERSAL_REPORT: 603,
  REVERSAL_CONFIRMED: 604,
  
  // Insurance and legal (700-799)
  INSURANCE_CLAIM: 700,
  INSURANCE_CLAIM_CONFIRMED: 701,
  INSURANCE_CLAIM_REJECTED: 702,
  LEGAL_CLAIM_FILED: 703,
  LEGAL_CLAIM_FILING_REPORT_RECEIVED: 704,
  
  // Assessments and methodologies (800-899)
  RISK_ASSESSMENT_UPDATED: 800,
  BIODIVERSITY_ASSESSMENT: 801,
  METHODOLOGY_UPDATE: 802,
  
  // Admin actions (900-999)
  // 900-904: entity management (mirrored in apps/admin/src/lib/audit.ts)
  ADMIN_USER_CREATED: 900,
  ADMIN_USER_UPDATED: 901,
  ADMIN_CARBON_SINK_CREATED: 902,
  ADMIN_CARBON_SINK_UPDATED: 903,
  ADMIN_SYSTEM_SETTINGS_UPDATED: 904,
  // 905-909: session events
  ADMIN_LOGIN: 905,
  ADMIN_LOGOUT: 906,
  ADMIN_EXPORT_DATA: 907,
  ADMIN_VIEW_SENSITIVE_DATA: 908,
  // 910-915: registry management
  ADMIN_CREATE_PROJECT_DEVELOPER: 910,
  ADMIN_UPDATE_PROJECT_DEVELOPER: 911,
  ADMIN_VIEW_PROJECT_DEVELOPER: 912,
  ADMIN_CREATE_BENEFICIARY: 913,
  ADMIN_UPDATE_BENEFICIARY: 914,
  ADMIN_VIEW_BENEFICIARY: 915,
  // 916-918: credit lifecycle
  ADMIN_ISSUE_CREDITS: 916,
  ADMIN_APPROVE_SINK: 917,
  ADMIN_REJECT_SINK: 918,
  // 919-923: settlement and trade operations
  ADMIN_INITIATE_SETTLEMENT: 919,
  ADMIN_ADVANCE_SETTLEMENT: 920,
  ADMIN_FAIL_SETTLEMENT: 921,
  ADMIN_MANUAL_SETTLEMENT: 922,
  ADMIN_CANCEL_TRADE: 923,

  // PD project edit audit trail (950-959)
  PROJECT_EDIT_POST_APPROVAL: 950,
  PD_VINTAGE_DECLARED: 951,
  PD_VINTAGE_ESTIMATE_UPDATED: 952,
  PD_EXTERNAL_REGISTRY_UPDATED: 953,
  PD_AMENDMENT_REQUESTED: 954,
  ADMIN_VINTAGE_CORRECTED: 955,  // Vintage record (year, creditClass) amended post-issuance
  CORRECTION_NOTE: 956,          // Admin note on irrecoverable error (retired credits)
} as const

// Event types at or above this value (900-999) are internal admin/audit
// records and must never surface on public registry pages or sitemaps.
export const ADMIN_EVENT_TYPE_MIN = 900

// Reverse mapping for converting integers back to string keys
export const EVENT_TYPE_NAMES = Object.fromEntries(
  Object.entries(EVENT_TYPES).map(([key, value]) => [value, key])
) as Record<number, keyof typeof EVENT_TYPES>

// Type for event type strings 
export type EventType = keyof typeof EVENT_TYPES
